import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  messageList: {
    position: "relative",
    overflowY: "auto",
    height: "100%",
    padding: theme.spacing(1, 0),
    ...theme.scrollbarStyles,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.01)"
        : "rgba(248, 250, 255, 0.72)",
  },
  inputArea: {
    position: "relative",
    padding: theme.spacing(1.25, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "#ffffff",
  },
  inputRoot: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "#f5f7fb",
    borderRadius: 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(0.5),
    transition: "background-color 120ms ease, box-shadow 120ms ease",
    "& fieldset": {
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(15,23,42,0.10)",
    },
    "&:hover fieldset": {
      borderColor: "#6172f3 !important",
    },
    "&.Mui-focused": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "#ffffff",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 0 0 3px rgba(97,114,243,0.25)"
          : "0 0 0 3px rgba(97,114,243,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6172f3 !important",
      borderWidth: "1px !important",
    },
  },
  inputElement: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  buttonSend: {
    backgroundColor: "#6172f3",
    color: "#fff",
    width: 40,
    height: 40,
    transition: "background-color 120ms ease, transform 80ms ease",
    "&:hover": {
      backgroundColor: "#4f5fe0",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "&.Mui-disabled": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(15,23,42,0.08)",
      color:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.3)"
          : "rgba(15,23,42,0.3)",
    },
  },
  boxLeft: {
    padding: "8px 12px 6px",
    margin: "8px 12px",
    position: "relative",
    backgroundColor: theme.palette.type === "light" ? "#ffffff" : "#1f2c3d",
    color: theme.palette.text.primary,
    maxWidth: 360,
    borderRadius: 12,
    borderBottomLeftRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,0.25)"
        : "0 1px 2px rgba(15,23,42,0.06)",
  },
  boxRight: {
    padding: "8px 12px 6px",
    margin: "8px 12px 8px auto",
    position: "relative",
    backgroundColor: theme.palette.type === "light" ? "#dcf8c6" : "#054640",
    color: theme.palette.text.primary,
    textAlign: "right",
    maxWidth: 360,
    borderRadius: 12,
    borderBottomRightRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0,0,0,0.25)"
        : "0 1px 2px rgba(15,23,42,0.06)",
  },
  senderName: {
    fontWeight: 600,
    fontSize: 12,
    opacity: 0.85,
    marginBottom: 2,
  },
  messageBody: {
    fontSize: 14,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.4,
  },
  messageTime: {
    fontSize: 10.5,
    opacity: 0.6,
    marginTop: 2,
  },
}));

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();
  const inputRef = useRef();

  const [contentMessage, setContentMessage] = useState("");

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const focusInput = () => {
    const refocus = () => {
      if (inputRef.current) {
        try {
          inputRef.current.focus();
        } catch (_) {
          /* ignore */
        }
      }
    };
    requestAnimationFrame(() => {
      refocus();
      setTimeout(refocus, 80);
    });
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser ? currentUser.unreads > 0 : 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Focus the textarea when the chat changes/opens
    focusInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat && chat.id]);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const send = () => {
    const trimmed = contentMessage.trim();
    if (trimmed === "") return;
    handleSendMessage(trimmed);
    setContentMessage("");
    focusInput();
  };

  const handleKeyDown = (e) => {
    // Enter sends; Shift+Enter inserts a new line
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Paper className={classes.mainContainer} elevation={0}>
      <div onScroll={handleScroll} className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            const isOwn = item.senderId === user.id;
            return (
              <Box
                key={key}
                className={isOwn ? classes.boxRight : classes.boxLeft}
              >
                {!isOwn && item.sender && (
                  <Typography
                    variant="subtitle2"
                    className={classes.senderName}
                  >
                    {item.sender.name}
                  </Typography>
                )}
                <Typography component="div" className={classes.messageBody}>
                  {item.message}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  className={classes.messageTime}
                >
                  {datetimeToClient(item.createdAt)}
                </Typography>
              </Box>
            );
          })}
        <div ref={baseRef}></div>
      </div>
      <div className={classes.inputArea}>
        <TextField
          fullWidth
          multiline
          maxRows={5}
          variant="outlined"
          size="small"
          placeholder="Digite uma mensagem... (Shift+Enter para nova linha)"
          inputRef={inputRef}
          autoFocus
          value={contentMessage}
          onChange={(e) => setContentMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            classes: { root: classes.inputRoot, input: classes.inputElement },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Enviar (Enter)">
                  <span>
                    <IconButton
                      onClick={send}
                      disabled={contentMessage.trim() === ""}
                      className={classes.buttonSend}
                      size="small"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </div>
    </Paper>
  );
}
